/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast } from 'lib/ToastHelper';
import React, { useEffect } from 'react';
import { companyIdAtom } from 'views/state';

import { filterValuesAtom, reportTypesAtom } from './state';

interface ApiError {
  data?: Record<string, string[]>;
}

export const useFilterConfig = (): FilterConfig => {
  const filterValues = useAtomValue(filterValuesAtom);
  const [reportTypes, setReportTypes] = useAtom(reportTypesAtom);
  const companyId = useAtomValue(companyIdAtom);

  useEffect(() => {
    const fetchReportTypes = async () => {
      try {
        const responseData = await callApi(`/companies/report-types/`, 'GET', {
          company_id: companyId,
        });
        if (responseData?.data?.report_types) {
          setReportTypes(responseData.data.report_types);
        } else {
          setReportTypes([]);
        }
      } catch (error) {
        const errorData = (error as ApiError)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load report types.';
        showFailureToast(errorMessage);
        setReportTypes([]);
      }
    };
    fetchReportTypes();
  }, [companyId, setReportTypes]);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      leftPanelFilters: ['is_active'],
      rightPanelFilters: ['subscription_type'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'is_active',
          label: 'Active / Inactive Members',
          tooltip: 'Filter by active members.',
          iconClass: 'fas fa-exclamation-triangle', // Icon added
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Active', value: 'true', deselectOthers: true },
            { label: 'Inactive', value: 'false', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
        new MultiSelectFilterConfig({
          id: 'subscription_type',
          label: 'Subscription Type',
          tooltip: 'Filter by subscription type (multi select).',
          iconClass: 'fas fa-exclamation-triangle', // Icon added
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...reportTypes.map((reportType) => ({
              label: reportType.name,
              value: reportType.name,
            })),
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
      ],
    });

    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, reportTypes]);

  return config;
};
